import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import KnowledgeBaseManager from "./KnowledgeBaseManager";
import MemoryViewer from "./MemoryViewer";
import { Icon } from "./Icon";

interface MemoryBankProps {
  authToken: string;
  botStatus: BotStatus[];
  userType?: "user" | "premium" | "vip" | "admin";
}

interface BotStatus {
  isActive: boolean;
  isConnected: boolean;
  personality: string;
}

const MemoryBank: React.FC<MemoryBankProps> = ({
  authToken,
  botStatus,
  userType,
}) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Get active subtab from URL
  const pathSegments = location.pathname.split("/");
  let activeSubTab = pathSegments[3] || "knowledge";

  // Handle sub-routes for user memories
  if (activeSubTab === "usermemories" || pathSegments[3] === "usermemories") {
    activeSubTab = "usermemories";
  }

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check if user has access to user memories (premium, vip, or admin)
  const effectiveUserType = userType || "user";
  const hasUserMemoriesAccess =
    effectiveUserType === "admin" ||
    effectiveUserType === "premium" ||
    effectiveUserType === "vip";

  // Sub-navigation style
  const subNavStyle = {
    display: "flex",
    gap: "20px",
    marginBottom: "24px",
    borderBottom: "1px solid #4a5568",
    paddingBottom: "12px",
  };

  const subNavLinkStyle = (isActive: boolean) => ({
    color: isActive ? "#9146FF" : "#cbd5e0",
    textDecoration: "none",
    fontSize: "16px",
    fontWeight: isActive ? "600" : "400",
    paddingBottom: "12px",
    borderBottom: isActive ? "2px solid #9146FF" : "2px solid transparent",
    transition: "all 0.2s",
  });

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
        width: "100%",
      }}
    >
      <h2 style={{ fontSize: "24px", marginBottom: "24px", color: "#e2e8f0" }}>
        <Icon
          name="brain"
          size={22}
          style={{ verticalAlign: "-3px", marginRight: "8px" }}
        />
        Memory Bank
      </h2>

      {/* Sub-navigation */}
      <div style={subNavStyle}>
        <Link
          to="/dashboard/memorybank/knowledge"
          style={subNavLinkStyle(activeSubTab === "knowledge")}
        >
          Knowledge Base
        </Link>
        <Link
          to="/dashboard/memorybank/usermemories"
          style={subNavLinkStyle(activeSubTab === "usermemories")}
        >
          {hasUserMemoriesAccess ? (
            "User Memories"
          ) : (
            <>
              <span style={{ marginRight: "4px" }}>
                <Icon name="lock" size={14} style={{ verticalAlign: "-2px" }} />
              </span>
              User Memories
            </>
          )}
        </Link>
      </div>

      {/* Sub-routes */}
      <Routes>
        <Route path="/" element={<Navigate to="knowledge" replace />} />
        <Route
          path="knowledge/*"
          element={
            <KnowledgeBaseManager
              authToken={authToken}
              userType={effectiveUserType}
            />
          }
        />
        {hasUserMemoriesAccess && (
          <>
            <Route
              path="usermemories"
              element={<Navigate to="search" replace />}
            />
            <Route
              path="usermemories/*"
              element={
                <Routes>
                  <Route
                    path="search"
                    element={
                      <MemoryViewer authToken={authToken} defaultMode="exact" />
                    }
                  />
                  <Route
                    path="query"
                    element={
                      <MemoryViewer
                        authToken={authToken}
                        defaultMode="semantic"
                      />
                    }
                  />
                  <Route path="" element={<Navigate to="search" replace />} />
                </Routes>
              }
            />
          </>
        )}
        {!hasUserMemoriesAccess && (
          <Route
            path="usermemories/*"
            element={
              <div
                style={{
                  maxWidth: "600px",
                  margin: "40px auto",
                  textAlign: "center",
                  padding: "40px",
                  backgroundColor: "#2d3748",
                  borderRadius: "12px",
                  border: "2px solid #4a5568",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "20px" }}>
                  <Icon name="lock" size={48} />
                </div>
                <h3
                  style={{
                    fontSize: "24px",
                    color: "#e2e8f0",
                    marginBottom: "16px",
                  }}
                >
                  User Memories is a Premium Feature
                </h3>
                <p
                  style={{
                    fontSize: "16px",
                    color: "#cbd5e0",
                    marginBottom: "24px",
                    lineHeight: "1.6",
                  }}
                >
                  Access and search through all user memories collected by HAL.
                  View detailed memory history, search by username or content,
                  and manage memory data.
                </p>
                <button
                  onClick={() => (window.location.href = "/dashboard/account")}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#9146FF",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#7c3aed";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(145, 70, 255, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#9146FF";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Icon
                    name="sparkles"
                    size={16}
                    style={{ verticalAlign: "-2px", marginRight: "4px" }}
                  />
                  Upgrade to Premium
                </button>
              </div>
            }
          />
        )}
      </Routes>
    </div>
  );
};

export default React.memo(MemoryBank);
